import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { useI18n } from '../../hooks/useI18n';
import SEO from '../../components/seo/SEO';
import AdSidebar from '../../components/ads/AdSidebar';
import { sidebarAds } from '../../data/staticAds';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const FAQ_AD_REQ = Object.freeze([{ placement: 'sidebar', limit: 2 }]);

const Title = styled.h1`
  margin: 0 0 2rem;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 800;
`;

const Q = styled.h2`
  margin: 1.5rem 0 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  color: ${({ theme }) => theme.colors.foreground};
`;

const A = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.65;
  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const FaqGrid = styled.div`
  display: grid;
  gap: 2rem;
  align-items: start;
  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) 300px;
  }
`;

const FaqMain = styled.div`
  min-width: 0;
`;

const FaqAside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (max-width: 1023px) {
    order: -1;
  }
`;

export default function FaqPage() {
  const dispatch = useDispatch();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const region = regionFromPathname(useLocation().pathname);
  const { t } = useI18n();
  const search = pathWithRegion('/search', region);

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('faq', FAQ_AD_REQ);
  const managedSidebars = useSelector((s) => selectAdPlacementList(s, 'faq', 'sidebar'));

  const faqSidebarLeft = useMemo(
    () => managedPlacementToAd(managedSidebars[0], pickStaticFallback(adsStaticEnabled, sidebarAds[0])),
    [managedSidebars, adsStaticEnabled],
  );
  const faqSidebarRight = useMemo(
    () =>
      managedPlacementToAd(
        managedSidebars[1],
        pickStaticFallback(adsStaticEnabled, sidebarAds[1] ?? sidebarAds[0]),
      ),
    [managedSidebars, adsStaticEnabled],
  );

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled) return;
    managedSidebars.forEach((item) => {
      if (!item?._id) return;
      const key = makeImpressionKey({
        campaign: item._id,
        page: 'faq',
        placement: 'sidebar',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: item._id,
            page: 'faq',
            placement: 'sidebar',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    });
  }, [dispatch, managedSidebars, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onFaqSidebarClick = useCallback(
    (campaignId) => () => {
      if (!sitePublicLoaded || !adsManagedEnabled || !campaignId) return;
      dispatch(
        trackAdClick({
          campaign: campaignId,
          page: 'faq',
          placement: 'sidebar',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const faqs = [
    { q: t('static.faq.q1', 'How do I find similar anime or TV shows?'), a: t('static.faq.a1', '') },
    { q: t('static.faq.q2', 'Is this using real data?'), a: t('static.faq.a2', '') },
    { q: t('static.faq.q3', 'How is this like Similaris?'), a: t('static.faq.a3', '') },
  ];

  return (
    <>
      <SEO
        region={region}
        path="/faq"
        title={t('static.faq.title', 'FAQ')}
        description={t('faq.meta.description', 'Frequently asked questions.')}
      />
      <PageLayout>
        <Container style={{ paddingTop: '2.5rem', paddingBottom: '3rem' }}>
          <FaqGrid>
            <FaqMain>
              <Title>{t('static.faq.title', 'FAQ')}</Title>
              {faqs.map((item) => (
                <div key={item.q}>
                  <Q>{item.q}</Q>
                  <A>{item.a}</A>
                </div>
              ))}
              <Q>{t('static.faq.qSearch', 'Where do I search?')}</Q>
              <A>
                {t('static.faq.aSearchPrefix', 'Go to')} <Link to={search}>{t('common.search', 'Search')}</Link>{' '}
                {t('static.faq.aSearchSuffix', 'and type a movie or show name.')}
              </A>
            </FaqMain>
            <FaqAside>
              <AdSidebar
                ad={faqSidebarLeft}
                pageKey="faq"
                onLinkClick={onFaqSidebarClick(faqSidebarLeft?.managedCampaignId)}
              />
              <AdSidebar
                ad={faqSidebarRight}
                pageKey="faq"
                onLinkClick={onFaqSidebarClick(faqSidebarRight?.managedCampaignId)}
              />
            </FaqAside>
          </FaqGrid>
        </Container>
      </PageLayout>
    </>
  );
}
